/**
 * The buyer's side of a sale, in words.
 *
 * A block of its own rather than keys on the collector's, because this screen is
 * about a thing being GIVEN to the reader rather than about anything they own.
 * Every other collector screen answers "what is mine"; this one answers "does
 * somebody propose to make this mine, and on what terms", and the register is
 * different enough that sharing copy would blur it.
 *
 * WHY IT EXISTS AT ALL. `ondo.effect_sale()` records a sale and OFFERS title —
 * it cannot move it, because the buyer has consented to nothing at the moment
 * the gallery presses Convert. Until this screen, `ondo.my_transfer_offers()`
 * and `GET /v1/transfers/offers` both answered and nothing read them, so an
 * on-ONDO sale parked at "awaiting buyer" with no control anywhere able to
 * settle it.
 */
export const OFFERS_COPY = {
  loading: "Loading what you have been offered…",
  errorTitle: "Can’t load your offers",

  /**
   * Says why the screen is empty in terms of somebody else's act, because there
   * is nothing the reader can do to put a row here.
   *
   * The gallery's inventory and the artist's settlements both make this same
   * argument in the same place: an empty screen with no explanation reads as
   * broken to the one party with no control to press.
   */
  empty: "Nobody has offered you title to a work.",
  emptyDetail:
    "When a gallery records a sale to your ONDO id, the work appears here with its price and any terms attached to it. Nothing moves into your collection until you accept it.",

  /** 'you have been offered 2 works'. */
  count: (n: number) => `${n} ${n === 1 ? "work" : "works"} offered to you`,

  lead: "A sale has been recorded against your ONDO id.",
  leadAction:
    "Title moves when you accept, and not before. Open any work for its full record first — an unanswered offer opens the record to you for exactly that reason.",

  soldOn: "Sold",
  price: "Price",
  seller: "Seller",
  /** Null when nobody brokered it — a sale made directly by the owner. */
  gallery: "Through",
  noGallery: "No gallery — sold directly by the owner",

  /**
   * The covenant, and this is the disclosure the screen exists to make.
   *
   * A buyer accepting a covenanted work is taking on a term that binds for
   * years and, when it locks, one that stops them selling the work on at all.
   * The gallery saw those terms as sliders it set; the buyer has never seen them
   * and cannot change them. Showing the price and hiding the encumbrance would
   * be the worst version of this screen.
   */
  covenantLegend: "Post-sale covenant",
  covenantNone: "None — the work is yours to sell on freely once title moves.",
  covenantShare: (pct: string, until: string) =>
    `${pct}% of any resale profit is owed back, on a resale before ${until}.`,
  covenantLocked:
    "LOCKED — you cannot transfer this work on until the term ends, or the gallery lifts the lock.",
  covenantOpen: "Not locked — you may still sell the work on within the term.",

  /**
   * THE COVENANT THAT CAME WITH THE PICTURE, which is a different covenant from
   * the one above and the whole of OND-85.
   *
   * The block above is the covenant THIS sale would create — what the buyer is
   * being asked to grant. This one is what an EARLIER sale already created and
   * what will bind the buyer's own onward sale. Before OND-85 the screen could
   * only see the first, so a gallery buying a covenanted work read "None — the
   * work is yours to sell on freely" about a picture that owed money.
   *
   * Said in the second person and in money, not in law: a dealer deciding
   * whether to buy for stock is deciding what the picture will cost them when
   * they sell it.
   */
  inheritedLegend: "What comes with the work",
  inheritedNone:
    "Nothing. No earlier sale of this work left an obligation that would follow it to you.",
  /** 'one covenant' / 'two covenants' reads better here than a digit. */
  inheritedCount: (n: number) =>
    `This work carries ${n === 1 ? "a resale covenant" : `${n} resale covenants`} from an earlier sale.`,
  inheritedShare: (pct: string, until: string) =>
    `${pct}% of the profit on your own onward sale is owed back, on any resale before ${until}.`,
  /**
   * Stands in for the end date in a case the database does not produce: the
   * figure is `max(ends_at)` over covenants that exist, so a count above zero
   * always has one. Here rather than inventing a date, because a wrong date on
   * this sentence is worse than an unspecific one.
   */
  inheritedUndated: "the term ends",
  inheritedLocked:
    "LOCKED — one of them forbids transferring this work on at all. You would not be able to sell it until the term ends or the holder lifts the lock.",
  inheritedOpen: "None of them prevents you selling it on.",
  /**
   * The profit is measured from the EARLIER sale's price, not from what this
   * buyer is about to pay — the single thing most likely to be assumed wrongly.
   *
   * No figure in it, because this row carries none — and since OND-206 there is
   * no figure on the detail read either, for a buyer. The earlier sale's price
   * comes back null from `GET /v1/works/:id/inherited-covenants` unless the
   * caller is a party to the sale that created the covenant, so a screen
   * rendering `originalMinor` must say WITHHELD and never "nothing is owed":
   * the obligation is the share and the splits, which are never withheld.
   */
  inheritedBasis:
    "The profit is measured from what the work sold for on that earlier sale — not from what you pay now.",
  inheritedDetail: "Read the full terms on the work’s record",

  /**
   * The acknowledgement, and it is a condition of the write rather than a
   * courtesy.
   *
   * `ondo.answer_transfer()` refuses an acceptance without it whenever a
   * covenant binds, and freezes what was shown into
   * `ondo.transfer_covenant_disclosures` when it has it. So this control is not
   * a confirmation dialog the client may skip — unchecked, the server says no,
   * which is exactly what OND-85 asked for: "an inherited obligation nobody was
   * shown is a dispute waiting to happen".
   */
  acknowledge:
    "I have read the obligations above and accept that they bind this firm on its own onward sale.",
  acknowledgeRequired:
    "Confirm you have read the obligations that come with this work before accepting it.",

  /**
   * WHICH HAT THIS PURCHASE IS BEING ACCEPTED IN — OND-184, the owner's own
   * question: "are they accepting the work as the director or the gallery".
   *
   * Drawn only when the buyer is NOT the reader, because that is the only case
   * worth a sentence: a collector with no firm sees no question, and a dealer's
   * own purchase behaves as it always did. It is a STATEMENT and not a choice,
   * and the reason is worth knowing before somebody turns it into a radio
   * group — `ondo.transfers.to_principal_id` was written when the sale was and
   * `ondo.assert_transfer_appended()` freezes it, so there is nothing here for
   * the reader to decide. What there is, is something they were never told: the
   * picture about to land is the firm's.
   *
   * No apostrophe-S on a name, because a firm's name may already end in one.
   */
  capacityLead: "Bought by the firm, not by you:",

  /**
   * The collection the work lands in, chosen by the buyer and nobody else.
   *
   * `ondo.answer_transfer()` requires one and refuses anything the caller does
   * not own. A registry that picked would be deciding a thing only the owner can
   * — which is the same sentence `AnswerTransferRequest` carries.
   *
   * SINCE OND-184 THE LIST IS THE BUYING PARTY'S, not everything the reader
   * owns. The door refuses the rest, so offering them would be offering a
   * refusal.
   */
  collectionLabel: "Accept into",
  collectionPlaceholder: "Choose a collection…",
  /**
   * The one state this screen cannot resolve for the reader.
   *
   * Accepting needs somewhere to put the work, and a collector with no
   * collection has nowhere. Says where to make one rather than disabling a
   * control with no explanation.
   */
  noCollections:
    "You have no collection to accept this into. Create one from Collections first — title has to land somewhere you own.",
  /**
   * The same absence about a FIRM — OND-184, and a different sentence because
   * the fix is a different act.
   *
   * A director with three collections of their own still has nowhere to put the
   * gallery's purchase, and the old sentence would have told them to make one
   * when they already had three. What they have to make is one in the firm's
   * name, which is a control Collections only grew on 14 October 2026.
   */
  noFirmCollections: (firm: string) =>
    `${firm} has no collection to accept this into. Create one in the firm's name from Collections first — the gallery's stock has to land somewhere the gallery owns.`,

  accept: "Accept title",
  accepting: "Accepting…",
  decline: "Decline",
  declining: "Declining…",

  /**
   * What declining does, said before it is pressed.
   *
   * There is no undo: `ondo.answer_transfer(p_accept => false)` sets the row to
   * `declined` and closes `ondo.transfer_offered_work_ids()` in the same
   * statement, so the picture stops being readable at the instant it is refused.
   */
  declineNote:
    "Declining ends the offer and tells the gallery. The work stops being readable to you at that moment, and only they can offer it again.",

  errors: {
    /** The row moved while the screen was open — withdrawn, or already answered. */
    gone: "That offer is no longer open. It may have been withdrawn, or already answered.",
    needsCollection: "Choose a collection to accept the work into.",
    refused: "The registry would not record that answer.",
  },
} as const;
